import { useEffect, useLayoutEffect, useState, useRef, useCallback } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import {
  LayoutDashboard,
  Calendar,
  Building2,
  Settings,
  HelpCircle,
  LogOut,
  FilePlus,
  Users,
  TrendingUp,
  Sparkles,
  Bell,
  User,
  UserPlus,
  Newspaper,
  Clock,
  ClipboardList,
  BookOpen,
} from "lucide-react"
import { useAuth } from "./AuthProvider"
import { useBranding } from "../lib/useBranding"
import { useSidebarCalendar } from "../lib/useSidebarCalendar"
import { useSidebarDigitalTime } from "../lib/useSidebarDigitalTime"
import { useProfile } from "../lib/useProfile"
import { supabase } from "../lib/supabase"
import { isDemoSite } from "../lib/isDemoSite"
import { useCortexModal } from "./CortexModalContext"
import { NotificationPanel } from "./NotificationPanel"
import { GlitchWord } from "./GlitchWord"
import { GlitchToggle } from "./GlitchToggle"
import { ROUTES } from "../lib/routes"
import { pathnameToPage } from "../lib/routes"
import { ROUTE_MAP } from "../lib/routeMapData"
import { btnNegativeNav } from "../lib/buttonTokens"
import { AnalogClock } from "./AnalogClock"
import { ClockInOut } from "./ClockInOut"
import { SidebarManifest } from "./SidebarManifest"
import { ClockSettingsModal } from "./ClockSettingsModal"
import { useGlassTokens } from "../lib/useAppBackground"

function SidebarClock({ showTime = true, showDate = true }: { showTime?: boolean; showDate?: boolean }) {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(t)
  }, [])
  const time = now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
  const date = now.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })
  if (!showTime && !showDate) return null
  return (
    <div className="min-w-0">
      {showTime && <p className="font-mono text-[13px] font-bold text-white/50 tabular-nums leading-tight">{time}</p>}
      {showDate && <p className="font-mono text-[10px] text-red-300/90 uppercase tracking-wider leading-tight">{date}</p>}
    </div>
  )
}

function SidebarLocation() {
  if (typeof Intl === "undefined" || !Intl.DateTimeFormat?.().resolvedOptions?.().timeZone) return null
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
  const city = (tz.split("/").pop() ?? tz).replace(/_/g, " ")
  let abbr = tz
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZone: tz, timeZoneName: "short" }).formatToParts(new Date())
    const a = parts.find((p) => p.type === "timeZoneName")?.value
    if (a && a !== tz) abbr = a
  } catch { /* */ }
  return (
    <span className="font-mono text-[9px] text-indigo-400/50 uppercase tracking-wider shrink-0" title={`${tz} (${abbr})`}>
      {city}
    </span>
  )
}

type NavItem = {
  path: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  show: (ctx: { role: string | null; isAdmin: boolean; isCreator: boolean; isOperator: boolean; isDemo: boolean }) => boolean
  group?: string
}

const NAV: NavItem[] = [
  { path: ROUTES.dashboard,        label: "Dashboard",   icon: LayoutDashboard, show: () => true,                              group: "home" },
  { path: ROUTES.properties,       label: "Properties",  icon: Building2,       show: () => true,                              group: "operations" },
  { path: ROUTES.adminProtocols,   label: "Protocols",   icon: ClipboardList,   show: ({ isAdmin }) => isAdmin,                group: "operations" },
  { path: ROUTES.adminPredictions, label: "Predictions", icon: TrendingUp,      show: ({ isAdmin }) => isAdmin,                group: "operations" },
  { path: ROUTES.schedule,         label: "Activity",    icon: Calendar,        show: () => true,                              group: "time" },
  { path: ROUTES.adminTime,        label: "Time",        icon: Clock,           show: ({ isAdmin }) => isAdmin,                group: "time" },
  { path: ROUTES.adminUsers,       label: "Users",       icon: Users,           show: ({ isAdmin }) => isAdmin,                group: "people" },
  { path: ROUTES.creatorLeads,     label: "Leads",       icon: UserPlus,        show: ({ isCreator }) => isCreator,            group: "people" },
  { path: ROUTES.myTrace,          label: "Trace",          icon: Newspaper, show: ({ isCreator }) => !isCreator, group: "trace" },
  { path: ROUTES.traceFeed,        label: "Platform Trace", icon: Newspaper, show: ({ isCreator }) => !isCreator, group: "trace" },
  { path: ROUTES.traceFeed,        label: "Platform Trace", icon: Newspaper, show: ({ isCreator }) => isCreator,  group: "trace" },
  { path: ROUTES.creatorDocs,      label: "Docs Editor", icon: BookOpen,        show: ({ isCreator }) => isCreator,            group: "trace" },
  { path: ROUTES.docs,             label: "Docs",        icon: HelpCircle,      show: () => true,                              group: "help" },
]

export function SidebarV2({
  isMobileOpen,
  onMobileClose,
  forceCollapsed = false,
  overlayDefault = false,
  isDocked = false,
  onUnreadCountChange,
}: {
  isMobileOpen: boolean
  onMobileClose: () => void
  forceCollapsed?: boolean
  overlayDefault?: boolean
  isDocked?: boolean
  onUnreadCountChange?: (count: number) => void
}) {
  const glass = useGlassTokens()
  const sidebarVisible = isMobileOpen || isDocked
  const navigate = useNavigate()
  const location = useLocation()
  const { signOut, role, isDemo, companyId } = useAuth()
  const { profile, loading } = useProfile()
  const cortexModal = useCortexModal()
  const [notificationOpen, setNotificationOpen] = useState(false)
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0)

  useEffect(() => {
    onUnreadCountChange?.(notificationUnreadCount)
  }, [notificationUnreadCount, onUnreadCountChange])
  const [closeButtonVisible, setCloseButtonVisible] = useState(false)
  const [cornerSquared, setCornerSquared] = useState(false)
  const notificationAnchorRef = useRef<HTMLButtonElement>(null)
  const closeButtonTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeStagedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // 1) button fades out → 2) corner rounds back → 3) sidebar slides away
  const handleStagedClose = () => {
    setCloseButtonVisible(false)
    if (closeStagedTimeoutRef.current) clearTimeout(closeStagedTimeoutRef.current)
    closeStagedTimeoutRef.current = setTimeout(() => {
      setCornerSquared(false)
      closeStagedTimeoutRef.current = setTimeout(() => {
        onMobileClose()
        closeStagedTimeoutRef.current = null
      }, 120)
    }, 150)
  }
  const { branding, refetch: refetchBranding } = useBranding()
  const [showSidebarCalendar] = useSidebarCalendar()
  const [showSidebarDigitalTime] = useSidebarDigitalTime()
  const [clockModalOpen, setClockModalOpen] = useState(false)
  // branding is source of truth; default true (on) when branding hasn't loaded yet
  const digitalTimeVisible = branding?.analogClockShowDigitalTime ?? showSidebarDigitalTime

  // For real tenants (non-demo), fetch companies.name so the sidebar shows
  // their actual company name — not the demo branding title ("Apex Facilities").
  const [tenantCompanyName, setTenantCompanyName] = useState<string | null>(null)
  const fetchTenantName = useCallback(async () => {
    if (isDemoSite() || !companyId) { setTenantCompanyName(null); return }
    const { data } = await supabase
      .from("companies")
      .select("name")
      .eq("id", companyId)
      .maybeSingle()
    setTenantCompanyName(data?.name ?? null)
  }, [companyId])

  useEffect(() => { void fetchTenantName() }, [fetchTenantName])

  useEffect(() => {
    if (!overlayDefault && !isDocked) return
    if (sidebarVisible) {
      setCloseButtonVisible(false)
      setCornerSquared(false)
      if (closeButtonTimeoutRef.current) clearTimeout(closeButtonTimeoutRef.current)
      closeButtonTimeoutRef.current = setTimeout(() => {
        setCornerSquared(true)
        requestAnimationFrame(() => setCloseButtonVisible(true))
        closeButtonTimeoutRef.current = null
      }, 200)
    } else {
      setCloseButtonVisible(false)
      setCornerSquared(false)
      if (closeButtonTimeoutRef.current) {
        clearTimeout(closeButtonTimeoutRef.current)
        closeButtonTimeoutRef.current = null
      }
    }
    return () => {
      if (closeButtonTimeoutRef.current) clearTimeout(closeButtonTimeoutRef.current)
      if (closeStagedTimeoutRef.current) clearTimeout(closeStagedTimeoutRef.current)
    }
  }, [sidebarVisible, overlayDefault, isDocked])

  useLayoutEffect(() => {
    if (!isMobileOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => { document.body.style.overflow = prev }
  }, [isMobileOpen])

  const parsed = pathnameToPage(location.pathname)
  const currentPage = parsed?.page ?? ""

  const activePageCode = (() => {
    const p = "/" + (currentPage === "home" ? "" : currentPage)
    const entry = ROUTE_MAP.find((r) => !r.isRedirect && (r.path === p || (p !== "/" && r.path.startsWith(p))))
    return entry?.pageCode ?? "0x00"
  })()

  const logoUrl = branding?.logoUrl ?? "/brand/macOS/app-icon-256.png"
  const dashboardTitle = branding?.dashboardTitle ?? "Luster"

  const displayName = loading
    ? "…"
    : profile?.name
      ? profile.name
      : profile?.email
        ? profile.email.split("@")[0]
        : "Guest"
  const initials =
    displayName && displayName !== "…" && displayName !== "Guest"
      ? displayName
        .split(/\s+/)
        .map((s) => s[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
      : null

  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    if (link && logoUrl) {
      link.href = logoUrl.startsWith("http") ? logoUrl : `${window.location.origin}${logoUrl}`
    }
  }, [logoUrl])

  const isActive = (path: string) => {
    if (path === ROUTES.dashboard || path === ROUTES.home) return !currentPage || currentPage === "home" || currentPage === "admin"
    // Paths that include a query string (e.g. /schedule?tab=time) need full match
    if (path.includes("?")) {
      const [pathBase, pathQuery] = path.split("?")
      if (location.pathname !== pathBase) return false
      const navParams = new URLSearchParams(pathQuery)
      const locParams = new URLSearchParams(location.search)
      for (const [k, v] of navParams.entries()) {
        if (locParams.get(k) !== v) return false
      }
      return true
    }
    // /schedule without ?tab=time should only be active when not on the time tab
    if (path === ROUTES.schedule) {
      return location.pathname === path && new URLSearchParams(location.search).get("tab") !== "time"
    }
    return location.pathname === path || location.pathname.startsWith(path + "/")
  }

  const handleNav = (path: string) => {
    navigate(path)
    onMobileClose()
  }

  const utilBtn = (active = false) => {
    const base = "relative w-full h-10 rounded-xl flex items-center justify-center transition-all border active:scale-95"
    return active
      ? `${base} bg-indigo-500/20 text-indigo-300 border-indigo-400/25`
      : `${base} border-transparent text-indigo-400/70 hover:bg-indigo-500/20 hover:text-indigo-300 hover:border-indigo-400/25`
  }

  const isCreator = role === "creator"
  const isAdmin = role === "admin" || role === "creator"
  const isOperator = role === "operator"
  const navCtx = { role, isAdmin, isCreator, isOperator, isDemo }

  const showCloseTab = overlayDefault || isDocked

  return (
    <>
      {/* Scrim */}
      <div
        className={`fixed inset-0 z-[60] bg-black/50 transition-opacity duration-150 will-change-[opacity] ${overlayDefault ? "" : "lg:hidden"} ${isMobileOpen && !isDocked ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        aria-hidden={!isMobileOpen || isDocked}
        onClick={onMobileClose}
      />

      {/* Sidebar + close tab wrapper — slides as one unit */}
      <div
        className={`
          fixed z-[70]
          ${forceCollapsed ? "top-0 left-0 h-full w-64" : "top-4 left-4 bottom-4 h-[calc(100dvh-2rem)] w-[19.5rem]"}
          transform transition-transform duration-200 ease-out will-change-transform
          ${forceCollapsed ? "" : overlayDefault && !isDocked ? "" : "lg:translate-x-0"}
          ${sidebarVisible ? "translate-x-0" : "-translate-x-[calc(100%+1rem)]"}
        `}
      >
        <aside
          className={`
            w-64 h-full overflow-hidden
            ${glass.cardBg} backdrop-blur-2xl text-white
            flex flex-col
            ${forceCollapsed ? "border-r border-white/[0.08]" : `rounded-xl border border-white/[0.08] transition-[border-radius] duration-120 ${cornerSquared ? "rounded-tr-none" : ""}`}
            ${sidebarVisible ? "shadow-lg" : "shadow-none"}
          `}
        >
          {/* Top: Brand mark — full width */}
          <div className="p-4 pb-2 min-w-0">
            <button
              type="button"
              className="group relative w-full cursor-pointer text-left rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3 transition-all hover:border-luster-400/15 hover:bg-white/[0.04] active:scale-[0.98]"
              onClick={() => handleNav(ROUTES.dashboard)}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 shrink-0 rounded-xl overflow-hidden border border-white/[0.10] bg-black/50 group-hover:border-luster-400/30 transition-colors">
                  <img src={logoUrl} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="min-w-0 flex-1 space-y-0.5">
                  <div className="font-mono text-[9px] flex items-center gap-1.5">
                    <span className="text-white/15 tabular-nums">{activePageCode}</span>
                    <span className="text-white/10">·</span>
                    <span className="text-luster-400/60 group-hover:text-luster-400 transition-colors">
                      <GlitchWord word="LUSTER" intensity={1} colorBase="violet" />
                    </span>
                  </div>
                  <div className="font-mono text-[9px] flex items-center gap-1.5">
                    <span className="text-white/10">←</span>
                    <span className="text-white/15">platform.init()</span>
                  </div>
                  {/* Creator/demo → dashboardTitle (branding scope label).
                      Real tenant users → companies.name from DB. */}
                  {(() => {
                    const label = (isCreator || isDemoSite()) ? dashboardTitle : tenantCompanyName
                    return label ? (
                      <div className="font-mono text-[9px] flex items-center gap-1.5">
                        <span className="text-white/10">↳</span>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 truncate group-hover:text-white/60 transition-colors" title={label}>
                          <GlitchWord word={label.toUpperCase()} intensity={1} colorBase="mixed" />
                        </p>
                      </div>
                    ) : null
                  })()}
                </div>
              </div>
            </button>
          </div>

          {/* Identity card */}
          <div className="px-4 py-2 shrink-0 space-y-2">
            {isDemo && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-indigo-500/10 border border-indigo-400/15">
                <Sparkles className="w-3 h-3 text-indigo-400 shrink-0" />
                <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-indigo-400/80 flex-1">Demo</span>
                <SidebarLocation />
              </div>
            )}

            {isOperator && <ClockInOut compact className="mb-2" />}

            <div className="flex flex-col gap-3 p-3.5 rounded-2xl border bg-white/[0.02] border-white/[0.05]">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => handleNav(ROUTES.settings)}
                    className="w-9 h-9 shrink-0 rounded-xl overflow-hidden border border-white/[0.08] bg-white/[0.04] hover:ring-2 hover:ring-teal-400/30 transition-all flex items-center justify-center group"
                    aria-label="Profile settings"
                  >
                    {!isDemo && profile?.avatarUrl ? (
                      <img src={profile.avatarUrl} alt="" className="w-full h-full object-cover" />
                    ) : !isDemo && initials ? (
                      <span className="font-mono text-[9px] font-bold text-white/70 group-hover:text-teal-400 transition-colors">{initials}</span>
                    ) : (
                      <User className="w-4 h-4 text-white/40 group-hover:text-teal-400 transition-colors" />
                    )}
                  </button>
                  <div className="h-6 w-px bg-white/[0.06]" />
                  <button
                    type="button"
                    onClick={() => setClockModalOpen(true)}
                    className="flex items-center gap-3 min-w-0 group/clock"
                    aria-label="Clock settings"
                    title="Clock settings"
                  >
                    <div
                      className={`w-9 h-9 shrink-0 rounded-xl overflow-hidden border border-white/[0.12] ${glass.modalBg} flex items-center justify-center group-hover/clock:ring-2 transition-all`}
                      style={{ "--tw-ring-color": `color-mix(in srgb, ${branding?.analogClockSecondsColor ?? "#6675c8"} 42%, transparent)` } as React.CSSProperties}
                    >
                      <AnalogClock
                        size={32}
                        fluidSeconds={branding?.analogClockFluidSeconds ?? false}
                        secondsHandColor={branding?.analogClockSecondsColor}
                        outlineColor={branding?.analogClockOutlineColor}
                        tickMarks={branding?.analogClockTickMarks ?? 12}
                      />
                    </div>
                    <SidebarClock
                      showTime={digitalTimeVisible}
                      showDate={showSidebarCalendar}
                    />
                  </button>
                </div>
              </div>

              <div className="h-px mx-1 bg-white/[0.06]" />

              {/* Utility row: help, Cortex, notifications */}
              <div className="grid grid-cols-3 gap-1 pt-1">
                <button
                  type="button"
                  onClick={() => { cortexModal?.openContact(); onMobileClose() }}
                  className={utilBtn()}
                  aria-label="Contact support"
                  title="Contact support"
                >
                  <HelpCircle className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => { cortexModal?.openCortex([]); onMobileClose() }}
                  className={utilBtn()}
                  aria-label="Cortex"
                  title="Cortex"
                >
                  <Sparkles className="w-4 h-4" />
                </button>
                <div className="relative group/notif">
                  <button
                    ref={notificationAnchorRef}
                    type="button"
                    onClick={() => setNotificationOpen((o) => !o)}
                    className={utilBtn(notificationOpen)}
                    aria-label="Notifications"
                    aria-expanded={notificationOpen}
                    title="Notifications"
                  >
                    <Bell className="w-4 h-4" />
                    {notificationUnreadCount > 0 && (
                      <span className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-luster-400 shadow-[0_0_6px_rgba(167,139,250,0.5)]" />
                    )}
                  </button>
                  <NotificationPanel
                    open={notificationOpen}
                    onClose={() => setNotificationOpen(false)}
                    anchorRef={notificationAnchorRef}
                    role={role}
                    onUnreadCountChange={setNotificationUnreadCount}
                    align="left"
                    onCloseSidebar={onMobileClose}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ── Nav ─────────────────────────────────────────────────────── */}
          <nav className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain py-4 px-3 min-w-0" aria-label="Main navigation">
            <SidebarManifest onNav={onMobileClose} tenantCompanyName={tenantCompanyName} />
            {isOperator && !isDemo && (
              <ul className="mt-1 space-y-0.5">
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      const path = location.pathname || "/"
                      const params = new URLSearchParams(location.search)
                      params.set("activity", "new")
                      navigate({ pathname: path, search: "?" + params.toString() })
                      onMobileClose()
                    }}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left font-mono text-[13px]
                      transition-colors duration-200 cursor-pointer
                      ${location.search.includes("activity=new") || location.search.includes("report=new")
                        ? "bg-teal-500/20 text-teal-300 border border-teal-400/30 font-bold"
                        : "text-white/50 hover:bg-white/[0.06] hover:text-white active:bg-white/[0.10]"
                      }
                    `}
                  >
                    <FilePlus className="w-4 h-4 shrink-0" />
                    New activity
                  </button>
                </li>
              </ul>
            )}
          </nav>

          {/* ── Footer: Settings + Logout + Luster — compact ────────── */}
          <div className="p-3 pt-0 space-y-0.5 shrink-0">
            {isDemo && (
              <a
                href={ROUTES.apply}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl font-mono text-[10px] font-bold uppercase tracking-wider bg-amber-400/10 border border-amber-400/20 text-amber-200 hover:bg-teal-400/10 hover:border-teal-400/20 hover:text-teal-200 transition-colors duration-700 cursor-pointer shadow-sm"
              >
                Request Access
              </a>
            )}
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={() => handleNav(ROUTES.settings)}
                className={`
                  flex-1 flex items-center gap-3 px-3 py-2 rounded-xl text-left font-mono text-[13px] transition-colors cursor-pointer
                  ${isActive(ROUTES.settings)
                    ? "bg-teal-500/20 text-teal-300 border border-teal-400/30 font-bold"
                    : "text-white/50 hover:bg-white/[0.06] hover:text-white active:bg-white/[0.10]"
                  }
                `}
              >
                <Settings className="w-4 h-4 shrink-0" />
                Settings
              </button>
              <button
                type="button"
                onClick={() => {
                  if (isDemo) { handleNav(ROUTES.logout) } else { signOut() }
                  onMobileClose()
                }}
                className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-red-400/60 hover:bg-red-500/10 hover:text-red-400 transition-colors cursor-pointer"
                aria-label="Log out"
                title="Log out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
            {branding?.poweredByUrl?.trim() ? (
              <a
                href={/^https?:\/\//i.test(branding.poweredByUrl) ? branding.poweredByUrl : `https://${branding.poweredByUrl.replace(/^\/*/, "").trim()}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-center gap-1.5 py-2 font-mono text-[9px] font-bold text-white/20 uppercase tracking-widest hover:text-teal-400/60 transition-colors"
              >
                <Sparkles className="w-2.5 h-2.5" />
                <GlitchWord word="Luster" intensity={1} colorBase="teal" />
              </a>
            ) : (
              <div className="flex items-center justify-center gap-1.5 py-2 font-mono text-[9px] font-bold text-white/15 uppercase tracking-widest" aria-hidden>
                <Sparkles className="w-2.5 h-2.5" />
                <GlitchWord word="Luster" intensity={1} colorBase="teal" />
              </div>
            )}
          </div>
        </aside>

        {/* Close tab — flush with sidebar edge, same glass surface */}
        {showCloseTab && (
          <button
            type="button"
            onClick={handleStagedClose}
            className={`
              absolute top-0 left-[calc(16rem-1px)]
              w-11 h-10 rounded-r-xl
              flex items-center justify-center
              ${glass.cardBg} backdrop-blur-2xl
              border border-l-0 border-white/[0.08]
              text-red-400/50 hover:bg-red-500/10 hover:text-red-400
              cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400/30
              transition-opacity duration-150
              ${closeButtonVisible ? "opacity-100" : "opacity-0 pointer-events-none"}
            `}
            aria-label="Close menu"
          >
            <GlitchToggle isOpen={true} className="text-base" />
          </button>
        )}
      </div>

      {clockModalOpen && (
        <ClockSettingsModal
          initialSecondsColor={branding?.analogClockSecondsColor}
          initialOutlineColor={branding?.analogClockOutlineColor}
          initialFluidSeconds={branding?.analogClockFluidSeconds ?? false}
          initialShowDigitalTime={branding?.analogClockShowDigitalTime ?? false}
          initialTickMarks={branding?.analogClockTickMarks ?? 12}
          onSave={() => { void refetchBranding() }}
          onClose={() => setClockModalOpen(false)}
        />
      )}
    </>
  )
}
